import React, { useEffect, useMemo, useState } from 'react';
import { useDocumentTitle } from '@mantine/hooks';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  Container,
  Group,
  Button,
  Text,
  Modal,
  Stack,
  TextInput,
  CopyButton,
  ActionIcon,
  Tooltip as MTooltip,
  Skeleton,
  Loader,
  createStyles,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconSettings, IconCopy, IconCheck } from '@tabler/icons-react';
import { HeaderMenu } from '../../components/HeaderMenu/HeaderMenu';
import { DashboardSwitcher } from '../../components/DashBoard/DashboardSwitcher';
import { SetupWizard, DashboardConfig } from '../../components/DashBoard/SetupWizard';
import { SettingsPanel, SettingsPanelConfig } from '../../components/DashBoard/SettingsPanel';
import { ScopeLine } from '../../components/DashBoard/ScopeLine';
import { BusinessDashboardView } from '../../components/DashBoard/BusinessDashboard/BusinessDashboardView';
import { MarketingDashboardView } from '../../components/DashBoard/MarketingDashboard/MarketingDashboardView';
import { SelectTimePicker, VisiLoader } from '../../components/GeneralComponent/GeneralComponent';
import { useDashboardProfiles } from '../../components/DashBoard/useDashboardProfiles';
import { useDashboardData } from '../../components/DashBoard/useDashboardData';
import { useFormOptions } from '../../components/DashBoard/useFormOptions';
import DashBoardApi from '../../api/DashBoardApi';
import GeneralComponentApi from '../../api/GeneralComponentApi';
import { visiUserSession } from '../../GeneralFunction';

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const useStyles = createStyles((theme) => ({
  shell: {
    borderRadius: 12,
    overflow: 'hidden',
    border: `1px solid ${theme.colorScheme === 'dark' ? theme.colors.dark[4] : '#E8E8E8'}`,
    background: theme.colorScheme === 'dark' ? theme.colors.dark[7] : '#FFFFFF',
    boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
  },
  topbar: {
    borderBottom: `1px solid ${theme.colorScheme === 'dark' ? theme.colors.dark[4] : '#EBEBEB'}`,
    padding: '18px 28px',
  },
  dashName: {
    fontSize: 17,
    fontWeight: 600,
    color: theme.colorScheme === 'dark' ? theme.white : '#1A1A1A',
  },
  snapshotBar: {
    borderBottom: `1px solid ${theme.colorScheme === 'dark' ? '#2C2E33' : '#EBEBEB'}`,
    background: theme.colorScheme === 'dark' ? '#1A1B1E' : '#F8FAFC',
    padding: '8px 22px',
  },
}));

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function DashBoard() {
  const { classes } = useStyles();
  const navigate = useNavigate();
  useDocumentTitle('Visiopt - Dashboard');

  const [selectedWebsite, setSelectedWebsite] = useState<string>(() => visiUserSession()?.website || '');

  // Time picker state (matches SelectTimePicker pattern used across the app)
  const [selectedTimePicker, setSelectedTimePicker] = useState<any>(visiUserSession()?.time || -1);
  const [selectedTimePickerFromDate, setSelectedTimePickerFromDate] = useState('');
  const [selectedTimePickerToDate, setSelectedTimePickerToDate] = useState('');

  // Setup wizard & settings panel
  const [wizardOpen, setWizardOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);

  // Phase 8 — Share snapshot modal state.
  // The snapshot itself is created server-side via createDashboardSnapshotLink,
  // which freezes the dashboard payload + assigns a 14-day expiry.
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [shareLink, setShareLink] = useState('');
  const [shareExpiresAt, setShareExpiresAt] = useState<string | null>(null);
  const [shareLoading, setShareLoading] = useState(false);


  // Time picker handlers (same pattern as CreateReport, ReviewingTest, etc.)
  const handleTimePickerChange = (value: any) => {
    setSelectedTimePicker(value);
  };
  const handleTimePickerFromDateChange = (value: any) => {
    setSelectedTimePickerFromDate(value || '');
  };
  const handleTimePickerToDateChange = (value: any) => {
    setSelectedTimePickerToDate(value || '');
  };

  const todayYMD = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };
  const getSelectedFromDate = () => {
    if (selectedTimePickerFromDate) {
      const [month, day, year] = selectedTimePickerFromDate.split('-');
      return `${year}-${month}-${day}`;
    }
    return todayYMD();
  };
  const getSelectedToDate = () => {
    if (selectedTimePickerToDate) {
      const [month, day, year] = selectedTimePickerToDate.split('-');
      return `${year}-${month}-${day}`;
    }
    return todayYMD();
  };

  // --- Custom Hooks ---
  const {
    profiles,
    activeProfile,
    activeId,
    setActiveId,
    loading: profilesLoading,
    createProfile,
    updateProfile,
    deleteProfile,
    setDefault,
  } = useDashboardProfiles(selectedWebsite);

  const {
    businessData,
    marketingOverview,
    marketingDiagnosis,
    loading: dataLoading,
    projectionPct,
    setProjectionPct,
  } = useDashboardData(activeProfile, selectedWebsite, selectedTimePicker, selectedTimePickerFromDate, selectedTimePickerToDate);

  const {
    campaignOptions,
    campaignOptionsLoading,
    goalOptions,
    revenueGoalOptions,
    reportOptions,
    fetchGoalOptions,
  } = useFormOptions(selectedWebsite);

  const [activeTestsCount, setActiveTestsCount] = useState<number>(0);
  const [activeTestsLoading, setActiveTestsLoading] = useState<boolean>(true);
  useEffect(() => {
    if (!selectedWebsite) {
      setActiveTestsCount(0);
      setActiveTestsLoading(false);
      return;
    }
    let cancelled = false;
    setActiveTestsLoading(true);
    GeneralComponentApi.getdashboardactivetestdropdown({ system_website_id: selectedWebsite })
      .then((res: any) => res.json())
      .then((data: any) => {
        if (cancelled) return;
        const list = Array.isArray(data?.data) ? data.data : [];
        setActiveTestsCount(list.length);
      })
      .catch(() => {
        if (cancelled) return;
        setActiveTestsCount(0);
      })
      .finally(() => {
        if (!cancelled) setActiveTestsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedWebsite]);

  // --- Derived values ---
  const dashboardType = activeProfile?.dashboard_type || 'business';
  // cost_threshold is a percentage (spec: default 20, range 5–50)
  const costThreshold = activeProfile?.settings?.cost_threshold ?? 20;
  const campaignIds = activeProfile?.settings?.selected_campaign_ids || [];
  const siteHasCampaigns = campaignOptions.length > 0;
  const fullPageLoading = profilesLoading || dataLoading || savingSettings;

  const dashboardEmptyStateLoading =
    profilesLoading ||
    dataLoading ||
    campaignOptionsLoading ||
    activeTestsLoading ||
    savingSettings;

  // Simple date range label for display
  const dateRangeLabel = (() => {
    const labels: Record<number, string> = {
      [-1]: 'All Time', 1: 'Today', 2: 'Yesterday', 3: 'Last 24 Hours',
      4: 'This Week', 5: 'Last 7 days', 6: 'Last 30 days', 12: 'Last 60 days', 13: 'Last 90 days', 7: 'This Month',
      8: 'Last month', 9: 'This Year', 10: 'Last Year',
    };
    const val = Number(selectedTimePicker);
    if (val === -2 && selectedTimePickerFromDate && selectedTimePickerToDate) {
      return `${selectedTimePickerFromDate} - ${selectedTimePickerToDate}`;
    }
    return labels[val] || 'Last 30 days';
  })();

  // Approximate number of days currently selected. Used by the Marketing
  // Dashboard to fire the spec-required <30-day warning banner.
  const selectedDayCount = useMemo(() => {
    const val = Number(selectedTimePicker);
    if (val === -2 && selectedTimePickerFromDate && selectedTimePickerToDate) {
      // Custom range: parse MM-DD-YYYY format.
      const parse = (s: string) => {
        const parts = s.split(/[-/]/);
        if (parts.length !== 3) return null;
        const [m, d, y] = parts.map((p) => parseInt(p, 10));
        if (!m || !d || !y) return null;
        return new Date(y, m - 1, d).getTime();
      };
      const fromMs = parse(selectedTimePickerFromDate);
      const toMs = parse(selectedTimePickerToDate);
      if (fromMs == null || toMs == null) return 30;
      return Math.max(1, Math.round((toMs - fromMs) / 86400000) + 1);
    }
    // Preset codes: -1 = All Time, 5 = Last 7, 6 = Last 30, etc. We return the
    // visible window approximation. Anything not enumerated defaults to 30.
    const dayMap: Record<number, number> = {
      [-1]: 9999, 1: 1, 2: 1, 3: 1, 4: 7, 5: 7, 6: 30,
      7: 30, 8: 30, 9: 365, 10: 365,
    };
    return dayMap[val] ?? 30;
  }, [selectedTimePicker, selectedTimePickerFromDate, selectedTimePickerToDate]);

  const primaryGoalName = useMemo(() => {
    if (businessData?.cards?.primary_goal_name) return businessData.cards.primary_goal_name;
    const goalIds = activeProfile?.settings?.selected_goal_ids;
    if (goalIds?.length) {
      const match = goalOptions.find((g) => g.value === goalIds[0]);
      if (match) return match.label;
    }
    return 'Primary Goal';
  }, [businessData, activeProfile, goalOptions]);

  const revenueGoalName = useMemo(() => {
    if (businessData?.cards?.revenue_event_name) return businessData.cards.revenue_event_name;
    const revId = activeProfile?.settings?.selected_revenue_goal_id;
    if (revId) {
      const match = goalOptions.find((g) => g.value === revId);
      if (match) return match.label;
    }
    return 'Not configured';
  }, [businessData, activeProfile, goalOptions]);

  const reportLabel = useMemo(() => {
    const reportId = activeProfile?.settings?.selected_report_id;
    if (!reportId) return 'Not configured';
    return reportOptions.find((r) => r.value === reportId)?.label
      || businessData?.scope?.report_name
      || 'Dashboard Report';
  }, [reportOptions, activeProfile, businessData]);

  // --- Navigation ---
  const goToReports = () => {
    const reportId = activeProfile?.settings?.selected_report_id;
    navigate(reportId ? `/campaign-report/${reportId}` : '/campaign-reports');
  };
  const goToTests = () => navigate('/review-tests');

  // --- Settings panel helpers ---
  const openSettings = async () => {
    if (!activeProfile) return;
    const ids = activeProfile.settings?.selected_campaign_ids || [];
    if (ids.length > 0) await fetchGoalOptions(ids);
    setSettingsOpen(true);
  };

  // --- Phase 8: Export PDF + Share snapshot handlers ---
  // PDF export uses the browser print dialog with a print-friendly stylesheet
  // injected via a transient `data-export-pdf` attribute on the body. This
  // avoids pulling in heavy html2canvas/jspdf dependencies for v1.
  const handleExportPdf = () => {
    document.body.setAttribute('data-export-pdf', '1');
    setTimeout(() => {
      window.print();
      document.body.removeAttribute('data-export-pdf');
    }, 50);
  };

  // Generate a frozen 14-day snapshot link via the existing backend endpoint.
  const handleShareLink = async () => {
    if (!activeProfile?.dashboard_profile_id) {
      notifications.show({ color: 'red', message: 'No active dashboard to share.' });
      return;
    }
    setShareLoading(true);
    setShareModalOpen(true);
    try {
      // Forward the current time picker state so the snapshot freezes the
      // SAME date range the user is looking at. Without these, the backend
      // would fall back to settings.date_range (typically -1 = all time)
      // and the snapshot numbers would diverge from the live screen.
      const timeParams: Record<string, any> = { time: Number(selectedTimePicker) || -1 };
      if (Number(selectedTimePicker) === -2 && selectedTimePickerFromDate && selectedTimePickerToDate) {
        timeParams.time = -2;
        timeParams.from = selectedTimePickerFromDate;
        timeParams.to = selectedTimePickerToDate;
      }
      const response = await DashBoardApi.createDashboardSnapshotLink({
        dashboard_profile_id: activeProfile.dashboard_profile_id,
        system_website_id: selectedWebsite,
        ...timeParams,
      });
      const data = await response.json();
      if (data?.status === 1 && data?.data?.snapshot_token) {
        const token = data.data.snapshot_token;
        const fullLink = `${window.location.origin}/dashboard/snapshot/${token}`;
        setShareLink(fullLink);
        if (data.data.expires_ts) {
          const d = new Date(Number(data.data.expires_ts) * 1000);
          setShareExpiresAt(d.toLocaleDateString());
        }
      } else {
        notifications.show({ color: 'red', message: data?.message || 'Failed to generate share link.' });
        setShareModalOpen(false);
      }
    } catch {
      notifications.show({ color: 'red', message: 'Failed to generate share link.' });
      setShareModalOpen(false);
    } finally {
      setShareLoading(false);
    }
  };

  // --- Render ---
  return (
    <>
      <HeaderMenu
        HeaderMenuValue={selectedWebsite}
        HeaderMenuOnChange={(v: any) => setSelectedWebsite(v)}
      />

      <Container size="xl" pb={30}>
        <Card className={classes.shell}>
          <VisiLoader VisiLoaderVisible={fullPageLoading} />

          {/* Top Bar */}
          <Group position="apart" className={classes.topbar} align="center" data-print-hide>
            <Group spacing="md">
              <Text className={classes.dashName}>
                {dashboardType === 'business' ? 'Business Dashboard' : 'Marketing Dashboard'}
              </Text>
              <DashboardSwitcher
                dashboards={profiles}
                activeDashboardId={activeId}
                onSelectDashboard={setActiveId}
                onCreateNew={async () => {
                  await fetchGoalOptions();
                  setWizardOpen(true);
                }}
                onDeleteDashboard={async (id) => deleteProfile(id)}
                isLoading={profilesLoading}
              />
            </Group>
            <Group spacing="xs">
              {/*
                Lock the time picker while data is loading so the user cannot
                queue up several rapid range switches while a fetch is in flight.
                `pointer-events: none` + reduced opacity is the least-invasive way
                to disable a third-party component that lacks a `disabled` prop.
              */}
              <div
                style={{
                  pointerEvents: dataLoading ? 'none' : 'auto',
                  opacity: dataLoading ? 0.6 : 1,
                  transition: 'opacity 120ms ease',
                }}
              >
                <SelectTimePicker
                  SelectTimePickerValue={selectedTimePicker}
                  SelectTimePickerOnChange={handleTimePickerChange}
                  SelectTimePickerFromDateOnChange={handleTimePickerFromDateChange}
                  SelectTimePickerToDateOnChange={handleTimePickerToDateChange}
                  SelectTestStartDate={getSelectedFromDate()}
                  SelectTestEndDate={getSelectedToDate()}
                />
              </div>
              <Button
                size="xs"
                variant="default"
                leftIcon={<IconSettings size={14} />}
                onClick={openSettings}
                disabled={dataLoading}
                sx={{ fontSize: 12 }}
              >
                Settings
              </Button>
            </Group>
          </Group>

          {/* Scope Line */}
          <ScopeLine
            campaigns={campaignIds}
            reportName={reportLabel}
            primaryGoal={primaryGoalName}
            revenueGoal={revenueGoalName}
            campaignOptions={campaignOptions}
            loading={profilesLoading || dataLoading}
          />

          {/* Dashboard Body */}
          {dashboardType === 'business' && (
            <BusinessDashboardView
              data={businessData}
              costThreshold={costThreshold}
              dateRangeLabel={dateRangeLabel}
              onNavigateToReports={goToReports}
              onNavigateToTests={goToTests}
              loading={dashboardEmptyStateLoading}
              siteHasCampaigns={siteHasCampaigns}
              activeTestsCount={activeTestsCount}
              onOpenSettings={openSettings}
            />
          )}

          {dashboardType === 'marketing' && (
            <MarketingDashboardView
              overviewData={marketingOverview}
              diagnosisData={marketingDiagnosis}
              primaryGoalName={primaryGoalName}
              costThreshold={costThreshold}
              dateRangeLabel={dateRangeLabel}
              selectedDayCount={selectedDayCount}
              campaignCount={campaignIds.length}
              projectionPct={projectionPct}
              onProjectionChange={setProjectionPct}
              onNavigateToReports={goToReports}
              onNavigateToTests={goToTests}
              onExportPdf={handleExportPdf}
              onShareLink={handleShareLink}
              loading={dashboardEmptyStateLoading}
              siteHasCampaigns={siteHasCampaigns}
              activeTestsCount={activeTestsCount}
              onOpenSettings={openSettings}
            />
          )}
        </Card>
      </Container>

      {/* Setup Wizard */}
      <SetupWizard
        opened={wizardOpen}
        onClose={() => setWizardOpen(false)}
        onCreateDashboard={async (config: DashboardConfig) => {
          await createProfile({
            dashboard_name: config.dashboard_name,
            dashboard_type: config.dashboard_type,
            campaign_ids: config.campaign_ids,
            goal_id: config.goal_id,
            revenue_goal_id: config.revenue_goal_id,
            set_as_default: config.set_as_default ? 1 : 0,
          });
        }}
        goalOptions={goalOptions}
        campaignOptions={campaignOptions}
        revenueGoalOptions={revenueGoalOptions}
        fetchGoalOptions={fetchGoalOptions}
        accountName={visiUserSession()?.website_name || ''}
        isLoading={profilesLoading}
      />

      {/* Settings Panel */}
      <SettingsPanel
        opened={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        dashboardName={activeProfile?.dashboard_name || ''}
        dashboardType={dashboardType as 'business' | 'marketing'}
        isDefaultDashboard={activeProfile?.is_default === 1}
        selectedCampaigns={campaignIds}
        primaryGoal={activeProfile?.settings?.selected_goal_ids?.[0] || ''}
        revenueGoal={activeProfile?.settings?.selected_revenue_goal_id || null}
        campaignReport={activeProfile?.settings?.selected_report_id || ''}

        costThreshold={costThreshold}
        varianceThreshold={activeProfile?.settings?.variance_threshold}
        projectionDefault={activeProfile?.settings?.projection_default}
        conversionRateBenchmark={(activeProfile?.settings as any)?.conversion_rate_benchmark ?? null}
        costPerGoalBenchmark={(activeProfile?.settings as any)?.cost_per_goal_benchmark ?? null}
        campaignOptions={campaignOptions}
        goalOptions={goalOptions}
        revenueGoalOptions={revenueGoalOptions}
        campaignReportOptions={reportOptions}
        onCampaignsChange={async (campaignIds) => {
          // Re-fetch the goal intersection for the new campaign set so the
          // Primary Goal / Revenue Goal dropdowns update live in the panel.
          await fetchGoalOptions(campaignIds);
        }}
        onSave={async (config: SettingsPanelConfig) => {
          if (!activeProfile?.dashboard_profile_id) return;
          setSavingSettings(true);
          setSettingsOpen(false);
          try {
            await updateProfile(activeProfile.dashboard_profile_id, {
              dashboard_name: config.dashboard_name,
              dashboard_settings: {
                cost_threshold: config.cost_threshold,
                variance_threshold: config.variance_threshold,
                projection_default: config.projection_default,
                conversion_rate_benchmark: config.conversion_rate_benchmark,
                cost_per_goal_benchmark: config.cost_per_goal_benchmark,
                selected_campaign_ids: config.campaign_ids,
                selected_goal_ids: [config.goal_id],
                selected_revenue_goal_id: config.revenue_goal_id,
                selected_report_id: config.campaign_report_id,
              },
            });
            if (config.set_as_default) {
              await setDefault(activeProfile.dashboard_profile_id);
            }
          } catch (err: any) {
            notifications.show({
              color: 'red',
              message: err?.message || 'Failed to save dashboard settings.',
            });
          } finally {
            setSavingSettings(false);
          }
        }}
        isLoading={profilesLoading}
      />

      {/* Phase 8: Share snapshot modal */}
      <Modal
        opened={shareModalOpen}
        onClose={() => setShareModalOpen(false)}
        title="Share dashboard snapshot"
        centered
        zIndex={1100}
        withinPortal
      >
        <Stack spacing="md">
          {shareLoading ? (
            <Stack spacing="md">
              <Group spacing="xs" align="center">
                <Loader size="xs" />
                <Text size="sm" color="dimmed">Generating snapshot link…</Text>
              </Group>
              {/* Skeleton mirrors the success layout so the modal doesn't
                  visibly jump when the link arrives. */}
              <Skeleton height={14} width="80%" radius="sm" />
              <Group spacing="xs" align="flex-end">
                <Skeleton height={36} radius="sm" style={{ flex: 1 }} />
                <Skeleton height={36} width={36} radius="sm" />
              </Group>
              <Skeleton height={11} width="60%" radius="sm" />
            </Stack>
          ) : shareLink ? (
            <>
              <Text size="sm">
                This link will expire on <strong>{shareExpiresAt || '14 days from now'}</strong>.
                Anyone with the link can view this snapshot.
              </Text>
              <Group spacing="xs" align="flex-end">
                <TextInput value={shareLink} readOnly style={{ flex: 1 }} />
                <CopyButton value={shareLink} timeout={2000}>
                  {({ copied, copy }) => (
                    <MTooltip label={copied ? 'Copied' : 'Copy link'} withArrow>
                      <ActionIcon size="lg" variant="default" onClick={copy}>
                        {copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
                      </ActionIcon>
                    </MTooltip>
                  )}
                </CopyButton>
              </Group>
              <Text size="xs" color="dimmed">
                The recipient sees the dashboard exactly as it appeared when this link was created. Data is frozen and will not update.
              </Text>
            </>
          ) : (
            <Text size="sm" color="dimmed">Failed to generate link.</Text>
          )}
        </Stack>
      </Modal>
    </>
  );
}
